import { skipToken } from '@reduxjs/toolkit/query';
import {
    CONTRACT_RATE,
    RouteCodeOptions,
    RouteCodes,
    UpdateProductTypeReasons,
} from '@strata/constants';
import { Checkbox } from '@strata/react-checkbox-list';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { Spinner } from '@strata/react-loading-animation';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ServicingRate } from '@strata/react-servicing-rate';
import { validationStrings } from '@strata/strata-ui-strings';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { updateProductType } from 'api/treatmentAPI';
import { ActionRateOverride } from 'components/referrals/dialogs/ActionRateOverride';
import { usePostDetermineReferralRatesQuery } from 'store/api/rateSetterApi';

const MethodOptions = RouteCodeOptions.filter(({ value }) =>
    [RouteCodes.ACCEPT, RouteCodes.CONTRACT, RouteCodes.DIRECT, RouteCodes.ROFR].includes(value),
);

export const UpdateActiveReferralDataProductType = ({ treatmentDetails = undefined }) => {
    const formik = useFormikContext();

    const {
        activeReferral,
        memberDetails,
        payerId,
        segmentId,
        productServiceOid,
        placeOfService,
        isBrand,
    } = treatmentDetails ?? {};

    const { benefitType } = activeReferral ?? {};
    const { memberId, firstName } = memberDetails ?? {};

    const { data: rateRule, isLoading } = usePostDetermineReferralRatesQuery(
        treatmentDetails && memberDetails
            ? {
                  payerId,
                  segmentId,
                  productServiceOid,
                  benefit: benefitType,
                  isBrand: !isBrand, // Show rates for product type referral will be updated to
                  memberId,
                  memberFirstName: firstName,
                  memberDateOfBirth: memberDetails.dateOfBirth,
                  routeCode: RouteCodes.ROFR,
                  placeOfService,
                  lineOfBusiness: memberDetails.lineOfBusiness,
              }
            : skipToken,
    );

    return isLoading ? (
        <Spinner className="padding__around--large" size={Spinner.Size.LARGE} />
    ) : (
        <>
            <SuggestSingleSelectFormik
                id="method"
                label="Method"
                required
                values={MethodOptions}
                selectedItem={MethodOptions.find(({ value }) => value === formik.values.method)}
                onChange={({ value }) =>
                    formik.setValues({
                        ...formik.values,
                        method: value,
                        ...ActionRateOverride.initialValues,
                        useContractRate: undefined,
                    })
                }
            />
            {formik.values.method !== RouteCodes.CONTRACT && (
                <>
                    <div className="margin__bottom--medium" style={{ display: 'flex' }}>
                        <DetailLabelValuePair
                            className="margin__right--x-large"
                            label={`${isBrand ? 'Generic' : 'Brand'} Default Rate`}
                            value={
                                <ServicingRate
                                    servicingRate={rateRule?.defaultRate}
                                    referentPriceType={rateRule?.referentPriceType}
                                />
                            }
                        />
                        <DetailLabelValuePair
                            label={`${isBrand ? 'Generic' : 'Brand'} Offer Rate`}
                            value={
                                <ServicingRate
                                    servicingRate={rateRule?.offerRate}
                                    referentPriceType={rateRule?.referentPriceType}
                                />
                            }
                        />
                    </div>
                    {!formik.values.useContractRate && (
                        <ActionRateOverride
                            label="Offer Rate Override"
                            helperText="This will override the offer rate, unless rate entered is less competitive than the Place of Service Override Rate."
                            rateRule={rateRule}
                        />
                    )}
                    <Checkbox
                        id="useContractRate"
                        label="Use Contract Rate"
                        onChange={(checked) =>
                            formik.setValues({
                                ...formik.values,
                                useContractRate: checked,
                                ...ActionRateOverride.initialValues,
                            })
                        }
                        checked={formik.values.useContractRate}
                    />
                </>
            )}
            <ReasonInput reasons={UpdateProductTypeReasons} />
        </>
    );
};

UpdateActiveReferralDataProductType.propTypes = {
    treatmentDetails: PropTypes.shape({
        treatmentId: PropTypes.string.isRequired,
        payerId: PropTypes.string.isRequired,
        productServiceOid: PropTypes.string.isRequired,
        placeOfService: PropTypes.string,
        isBrand: PropTypes.bool,
        benefitType: PropTypes.string,
        activeReferral: PropTypes.shape({
            servicingSupplierNpi: PropTypes.string,
        }),
        memberDetails: PropTypes.shape({
            memberId: PropTypes.string,
            firstName: PropTypes.string,
            memberState: PropTypes.string,
            dateOfBirth: PropTypes.string,
            lineOfBusiness: PropTypes.string,
            lobSegment: PropTypes.string,
        }),
    }),
};

UpdateActiveReferralDataProductType.initialValues = () => ({ method: RouteCodes.ROFR });

UpdateActiveReferralDataProductType.validationSchema = {
    method: Yup.string()
        .oneOf(Object.values(RouteCodes))
        .required(validationStrings.requiredField.errorMessage),
    useContractRate: Yup.bool(),
    ...ActionRateOverride.validationSchema,
    ...ReasonInput.validationSchema,
};

UpdateActiveReferralDataProductType.onSubmit = async ({
    values,
    treatmentDetails,
    username,
    rateRuleOppositeProductType,
}) => {
    const { treatmentId, payerId, isBrand } = treatmentDetails ?? {};

    const offerRateOverride =
        values.method === RouteCodes.CONTRACT || values.useContractRate
            ? CONTRACT_RATE
            : ActionRateOverride.submitValue(values);

    await updateProductType({
        treatmentId,
        payerId,
        updatedIsBrand: !isBrand,
        username,
        routeCode: values.method,
        offerRateOverride,
        referentPriceType: rateRuleOppositeProductType?.referentPriceType,
        priceSource: rateRuleOppositeProductType?.priceSource,
        isFrozen: rateRuleOppositeProductType?.isFrozen,
        ...ReasonInput.submitValues(values),
    });
};
